const post = {
  slug: "instagram-checkout-ended-sell-in-dms",
  title: "Instagram Checkout Has Ended: How to Sell in DMs Now",
  seoTitle: "Instagram Checkout Ended: How to Sell in DMs",
  description:
    "Meta moved Shops to website checkout, so Instagram no longer takes payments in the app. See what changed and four ways small sellers can get paid now.",
  excerpt:
    "Instagram no longer takes payments inside the app. Here is what changed, what stayed the same and the practical options a small seller has for getting paid.",
  category: "Selling on Instagram",
  tags: ["Instagram checkout ended", "Sell in Instagram DMs", "Instagram Shops website checkout"],
  date: "2026-10-28",
  cover: {
    src: "/blog-images/instagram-checkout-ended-sell-in-dms-cover.png",
    alt: "A phone showing an Instagram DM where a seller sends a secure payment link to a customer",
  },
  blocks: [
    {
      type: "p",
      text:
        "If you ever tagged products on Instagram and hoped customers would pay without leaving the app, that option is gone. Meta moved Shops away from checkout on Facebook and Instagram, and buyers who tap to buy are now sent to a seller\u2019s own website to pay.",
    },
    {
      type: "p",
      text:
        "This guide explains what Meta changed, what did not change, and the options a small seller has now. If most of your sales happen in DMs, the news is less dramatic than it sounds, but it is worth checking that your payment route still works.",
    },

    { type: "h2", text: "What Meta changed" },
    {
      type: "p",
      text:
        "Meta announced in June 2025 that Shops would gradually switch from checkout on Facebook and Instagram to website checkout. Meta\u2019s help pages now state that Shops on both platforms use website checkout. Customers still discover products on Instagram, but they finish the purchase on the seller\u2019s own site.",
    },
    {
      type: "p",
      text:
        "Several things that depended on in-app checkout went with it:",
    },
    {
      type: "ul",
      items: [
        "**Payments inside the app.** You can no longer take transactions inside your Shop on Instagram.",
        "**Order management in Commerce Manager.** For new orders, Meta no longer shows order status or lets you manage orders in bulk there.",
        "**Disputes.** Meta no longer offers chargeback or dispute resolution for Shops, so your payment provider handles them.",
        "**Some ad metrics.** Events tied to Meta\u2019s own checkout, such as purchases completed on Instagram, are no longer tracked once a Shop switches.",
      ],
    },

    { type: "h2", text: "What did not change" },
    {
      type: "ul",
      items: [
        "**People still message businesses.** DMs, Story replies and comment replies work as before.",
        "**Product tags still point customers to you.** They now lead to a website rather than an in-app payment screen.",
        "**You can still take payment through a link.** Anything outside Instagram\u2019s own checkout, from a payment link to a bank transfer, is unaffected.",
      ],
    },
    {
      type: "p",
      text:
        "In other words, Instagram remains a place where customers find you and talk to you. It stopped being a place where they pay. That is why the quality of your DM-to-payment route matters more than it used to.",
    },

    { type: "h2", text: "Does this affect you?" },
    {
      type: "p",
      text:
        "It depends on how you sell today.",
    },
    {
      type: "ul",
      items: [
        "**You use a Shop with product tags.** Check that your Shop has a working checkout link on your own website. Without one, tagged products have nowhere useful to send buyers.",
        "**You sell through DMs and comments.** Your sales path did not break, but you still need a clean way to collect the order details and the payment.",
        "**You have no website.** You are not stuck, because several options below work without one. See also our guide to [selling on Instagram without a website](/blog/sell-on-instagram-without-a-website).",
      ],
    },

    { type: "h2", text: "Four ways to get paid now" },
    {
      type: "table",
      headers: ["Option", "How it works", "Best for"],
      rows: [
        ["Your own website checkout", "Customers tap a link and pay on your store", "Sellers who already run a website and a full catalog"],
        ["A payment link in the DM", "You send a link for one order and the buyer pays there", "A handful of orders a week"],
        ["Checkout inside the chat", "The catalog, cart, address and payment happen in the conversation", "Sellers who get many DMs and want less typing"],
        ["A shareable storefront link", "One web link that works on any platform, not only Instagram", "Sellers who also sell on YouTube, WhatsApp or Telegram"],
      ],
    },
    {
      type: "p",
      text:
        "Each option has a cost. A website needs setup and upkeep. Single payment links need you to create and track each one by hand. Chat checkout and storefront links need a tool, but they save the most time once your volume grows.",
    },

    { type: "h2", text: "Choose a payment route that confirms itself" },
    {
      type: "p",
      text:
        "Whichever route you pick, think about how you will know that a payment really arrived. A payment gateway confirms payments automatically, while manual UPI or bank transfers need you to check your own account. Our guides on [UPI versus a payment gateway](/blog/accept-payments-instagram-upi-vs-gateway) and [avoiding fake payment screenshots](/blog/avoid-fake-payment-screenshots-instagram-sellers) cover the trade-offs, and [taking payments on Instagram in India](/blog/take-payments-on-instagram-india) lists every option.",
    },
    {
      type: "chat",
      shop: "yourshop",
      title: "From enquiry to payment link",
      caption: "Illustrative example. Use your own wording.",
      messages: [
        { from: "buyer", text: "Is the green kurta available in M?" },
        { from: "shop", text: "Yes! M is in stock. It is \u20B91,499 with free delivery. Shall I send the payment link?" },
        { from: "buyer", text: "Yes please" },
        { from: "shop", text: "Here is your secure payment link. I will confirm your order as soon as it goes through." },
      ],
    },

    { type: "h2", text: "How Checkout DM fits in" },
    {
      type: "p",
      text:
        "Checkout DM is Instagram-first and is built for this situation. The catalog, size choice, cart, delivery address and payment all happen inside the DM, so there is no website to build. Buyers can pay through Razorpay, Stripe or PayPal, or by manual UPI, QR code or bank transfer that you confirm. Payments go to your own account, and Checkout DM does not take a commission on your orders.",
    },
    {
      type: "p",
      text:
        "Checkout DM also gives you a Storefront, a website link you can share on YouTube, WhatsApp, Telegram, Facebook or any website, so customers who never open Instagram can still buy your digital or physical products. Our guide to [selling on Instagram without losing DM orders](/blog/sell-on-instagram-dms-without-losing-orders) shows how the whole flow works.",
    },

    { type: "h2", text: "A short checklist for this week" },
    {
      type: "ol",
      items: [
        "**Test your own route.** Place an order from a second Instagram account and see exactly what a customer sees.",
        "**Check where payments land.** Make sure you know which account receives the money and how you will confirm it.",
        "**Keep your own records.** Meta no longer manages orders for new Shop orders, so note each order, payment and shipment yourself or in your tool.",
        "**Plan for disputes.** Find out how your payment provider handles a chargeback.",
        "**Update your bio and replies.** Tell customers how to order and pay, in one clear sentence.",
      ],
    },
    {
      type: "callout",
      tone: "lilac",
      title: "Check Meta\u2019s current rules",
      text:
        "Meta updates its commerce policies from time to time. This guide reflects Meta\u2019s published statements as of October 2026, so confirm the current details in Meta\u2019s help centre before you rely on them for something important.",
    },
  ],
  related: [
    { to: "/pricing", title: "Pricing", description: "Compare plans, from the Free plan to Pro." },
    { to: "/razorpay-keys-guide", title: "Razorpay Keys Guide", description: "Connect a gateway so payments confirm automatically." },
    { to: "/faq", title: "Frequently Asked Questions", description: "Answers on payments, shipping and returns." },
  ],
  faqs: [
    {
      q: "Can customers still buy inside Instagram?",
      a: "Not through Instagram\u2019s own checkout. Meta states that Shops on Facebook and Instagram now use website checkout, so buyers are sent to the seller\u2019s website to pay. You can still sell through DMs and payment links.",
    },
    {
      q: "Do I need a website to sell on Instagram now?",
      a: "Not necessarily. A website is one option, but you can also take orders and payments through payment links, chat checkout or a shareable storefront link, depending on the tool you use.",
    },
    {
      q: "Who handles chargebacks and disputes now?",
      a: "Meta no longer offers chargeback or dispute resolution for Shops. Your payment provider or bank handles disputes, so check how yours works before you need it.",
    },
    {
      q: "Does Checkout DM need a website?",
      a: "No. Checkout DM runs the catalog, cart, address collection and payment inside the Instagram DM, and its Storefront gives you a shareable web link if you also want to sell elsewhere.",
    },
  ],
};

export default post;
